// Built-in simple field types for the shared registry. Ported verbatim from the
// field app's renderField() so the rendered DOM (tags + classes + behaviour) is
// identical — only the data plumbing changed from global `state` to `ctx`.

import { t } from '../core/i18n.js';
import { widthSixths } from './builder-model.js';

const INPUT_TYPES = ['text', 'number', 'date', 'tel', 'email'];

// Fractional widths are honoured only on short inputs (widthSixths applies
// the capable-type gate) — pairing is pure CSS grid (#dynamicFields is 6
// columns; a third spans 2, a half 3, two-thirds 4, everything else all 6).
// The phone snap rule (≤480px: thirds→half, two-thirds→full) lives in CSS.
const _SX_CLASS = { 2: 'field-group--third', 3: 'field-group--half', 4: 'field-group--wide' };

function _wrapper(field) {
  const wrapper = document.createElement('div');
  wrapper.className = 'field-group';
  const sxClass = _SX_CLASS[widthSixths(field)];
  if (sxClass) wrapper.classList.add(sxClass);
  wrapper.dataset.fieldId = field.id;   // so conditional logic can show/hide it
  const label = document.createElement('label');
  label.className = 'field-label';
  label.htmlFor = `field_${field.id}`;
  label.textContent = field.label + (field.required ? ' *' : '');
  wrapper.appendChild(label);
  // Guidance line under the label. The builder edits `help` and the AI
  // converter emits it since day one — this is where it finally shows.
  if (field.help) {
    const help = document.createElement('p');
    help.className = 'field-help';
    help.textContent = field.help;
    wrapper.appendChild(help);
  }
  return { wrapper, label };
}

// A string of spaces is empty too — a single space used to satisfy any
// required text, textarea or select (stranger sweep, 2026-10-17).
export function isEmptyValue(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v == null || v === '' || (Array.isArray(v) && v.length === 0);
}
function _requiredMsg(field, value) {
  if (!field.required) return null;
  return isEmptyValue(value) ? `Please fill in: ${field.label}` : null;
}
// Number fields never go below `min`: 0 unless the schema says otherwise
// (`min: null` = any sign, e.g. a temperature). The HTML min only steers the
// picker — typing is not blocked by it — so the refusal lives in validate.
function _numberMin(field) {
  if (field.min === null) return null;
  return field.min == null ? 0 : Number(field.min);
}
function _numberMsg(field, value) {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(value), min = _numberMin(field);
  if (!Number.isFinite(n)) return t('field.notANumber', { label: field.label });
  if (min != null && n < min) {
    return min === 0 ? t('field.negative', { label: field.label }) : t('field.belowMin', { label: field.label, min });
  }
  return null;
}

const inputHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const input = document.createElement('input');
    input.className = 'field-input';
    input.type = field.type || 'text';
    if (field.type === 'number') {
      const min = _numberMin(field);
      if (min != null) input.min = String(min);
      input.inputMode = 'decimal';
    }
    input.placeholder = field.placeholder || '';
    input.value = ctx.get(field.id) || '';
    input.addEventListener('input', e => ctx.set(field.id, e.target.value));
    _applyAutofill(field, input, ctx);
    input.id = `field_${field.id}`;
    wrapper.appendChild(input);
    ctx.container.appendChild(wrapper);
  },
  validate: (field, value) => _requiredMsg(field, value)
    || (field.type === 'number' ? _numberMsg(field, value) : null),
};

const textareaHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const ta = document.createElement('textarea');
    ta.className = 'field-input field-textarea';
    ta.id = `field_${field.id}`;
    // minH is the printed writing space in mm (sheet editor's height handle);
    // ~8mm of paper per text row is the same proportion the PDF prints.
    ta.rows = field.minH ? Math.max(3, Math.round(field.minH / 8)) : 3;
    ta.placeholder = field.placeholder || '';
    ta.value = ctx.get(field.id) || '';
    ta.addEventListener('input', e => ctx.set(field.id, e.target.value));
    const taWrap = document.createElement('div');
    taWrap.className = 'textarea-wrap';
    taWrap.appendChild(ta);
    const mic = ctx.helpers?.micButton?.(ta, field.id);
    if (mic) taWrap.appendChild(mic);
    wrapper.appendChild(taWrap);
    ctx.container.appendChild(wrapper);
  },
  validate: _requiredMsg,
};

const selectHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const input = document.createElement('select');
    input.className = 'field-input field-select';
    const blank = document.createElement('option');
    blank.value = ''; blank.textContent = 'Select...';
    input.appendChild(blank);
    (field.options || []).forEach(opt => {
      const o = document.createElement('option');
      o.value = opt; o.textContent = opt;
      if (ctx.get(field.id) === opt) o.selected = true;
      input.appendChild(o);
    });
    input.addEventListener('change', e => ctx.set(field.id, e.target.value));
    input.id = `field_${field.id}`;
    wrapper.appendChild(input);
    ctx.container.appendChild(wrapper);
  },
  validate: _requiredMsg,
};

const radioHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const group = document.createElement('div');
    group.className = 'radio-group';
    (field.options || []).forEach(opt => {
      const item = document.createElement('label');
      item.className = 'radio-item';
      const rb = document.createElement('input');
      rb.type = 'radio'; rb.name = `field_${field.id}`; rb.value = opt;
      rb.checked = ctx.get(field.id) === opt;
      rb.addEventListener('change', () => ctx.set(field.id, opt));
      item.appendChild(rb);
      item.appendChild(document.createTextNode(' ' + opt));
      group.appendChild(item);
    });
    wrapper.appendChild(group);
    ctx.container.appendChild(wrapper);
  },
  validate: _requiredMsg,
};

const checkboxGroupHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const grid = document.createElement('div');
    grid.className = 'checkbox-grid';
    const selected = ctx.get(field.id) || [];
    (field.options || []).forEach(opt => {
      const item = document.createElement('label');
      item.className = 'checkbox-item';
      const cb = document.createElement('input');
      cb.type = 'checkbox'; cb.value = opt; cb.checked = selected.includes(opt);
      cb.addEventListener('change', () => {
        const cur = ctx.get(field.id) || [];
        ctx.set(field.id, cb.checked ? [...cur, opt] : cur.filter(v => v !== opt));
      });
      item.appendChild(cb);
      item.appendChild(document.createTextNode(' ' + opt));
      grid.appendChild(item);
    });
    wrapper.appendChild(grid);
    ctx.container.appendChild(wrapper);
  },
  validate: _requiredMsg,
};

const sectionHandler = {
  render(field, ctx) {
    const div = document.createElement('div');
    div.className = 'section-header';
    div.style.marginTop = '20px';
    div.textContent = field.label;
    ctx.container.appendChild(div);
  },
  validate() { return null; },
};

// Photo field (Onda 3d) — a labelled capture that feeds the form's existing photo
// pipeline. In the field app, ctx.helpers.pickPhotos opens the camera/picker and
// appends to state.photos (so it's saved + embedded in the PDF like any photo);
// in the admin builder the helper is absent, so it shows a disabled placeholder.
const photoHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'field-input field-photo-btn';
    btn.textContent = '📷 ' + (field.placeholder || 'Add photo');
    const status = document.createElement('span');
    status.className = 'field-photo-status';
    if (ctx.helpers && ctx.helpers.pickPhotos) {
      btn.addEventListener('click', () => ctx.helpers.pickPhotos(n => {
        status.textContent = n ? ` ${n} photo(s)` : '';
        // Mark the field answered so a required photo can pass validation —
        // the photos themselves live in the app's global photo pool, this
        // value is just the count (audit fix: required photo used to be an
        // unsubmittable invisible wall).
        ctx.set(field.id, n || null);
      }));
    } else {
      btn.disabled = true;
    }
    wrapper.appendChild(btn);
    wrapper.appendChild(status);
    ctx.container.appendChild(wrapper);
  },
  validate: _requiredMsg,
};

// Status-grid checklist (Onda 1): each item answered with one of a shared
// option set. Value = index-aligned [{item, answer}] — labels may repeat, so
// no slug keys; `item` embedded keeps submissions.data self-describing after
// schema edits (qaqc-table precedent). Length mismatch = schema was edited →
// re-init (qaqc precedent).
// The status vocabulary a checklist answers with. A schema may override it
// (site_orientation_record uses Covered/N/A); when it doesn't, THIS is the
// default — and every reader of an answer must use the SAME one: the field on
// screen, the required-comment gate, and the PDF. A second copy of this list
// living in the PDF generator is what printed 18 answer-less rows on a signed
// equipment log (2026-07-27).
export const CHECKLIST_DEFAULT_OPTIONS = Object.freeze(['OK', 'Deficient', 'N/A']);

export function checklistOptions(field) {
  const o = field && field.options;
  return (Array.isArray(o) && o.length) ? o : [...CHECKLIST_DEFAULT_OPTIONS];
}

// Answers that demand an explanation (qaqc 'fail' rule generalized): schema
// commentOn wins; default = the SECOND status option (Deficient in
// OK/Deficient/N/A, Fail in Pass/Fail, No in Yes/No/N/A).
export function checklistFlagged(field) {
  const c = field && field.commentOn;
  if (Array.isArray(c) && c.length) return c;
  const opts = checklistOptions(field);
  return opts[1] ? [opts[1]] : [];
}

const checklistHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const items = field.items || [];
    const options = checklistOptions(field);

    let value = ctx.get(field.id);
    if (!Array.isArray(value) || value.length !== items.length) {
      value = items.map(it => ({ item: it, answer: null }));
    } else {
      value = value.map((row, i) => ({
        item: items[i],
        answer: (row && row.answer) || null,
        ...(row && row.comment ? { comment: row.comment } : {}),
      }));
    }
    ctx.set(field.id, value);

    const flagged = checklistFlagged(field);

    const bar = document.createElement('div');
    bar.className = 'cl-toolbar';
    const markAll = document.createElement('button');
    markAll.type = 'button';
    markAll.className = 'cl-markall';
    markAll.textContent = `Mark all ${options[0]}`;
    const counter = document.createElement('span');
    counter.className = 'cl-count';
    const syncCount = () => {
      counter.textContent = `${value.filter(r => r.answer).length}/${items.length}`;
    };
    bar.appendChild(markAll);
    bar.appendChild(counter);
    wrapper.appendChild(bar);

    const rowBtns = [];   // rowBtns[i] = [{el, opt}] for repaint on mark-all
    const list = document.createElement('div');
    list.className = 'cl-list';
    items.forEach((it, i) => {
      const row = document.createElement('div');
      row.className = 'cl-row' + (i % 2 ? ' cl-row-alt' : '');
      const head = document.createElement('div');
      head.className = 'cl-row-head';
      const num = document.createElement('div');
      num.className = 'cl-num';
      num.textContent = i + 1;
      const txt = document.createElement('div');
      txt.className = 'cl-item-text';
      txt.textContent = it;
      head.appendChild(num);
      head.appendChild(txt);
      row.appendChild(head);

      const btns = document.createElement('div');
      btns.className = 'cl-btns';

      // Comment box for flagged answers ("Deficient" etc.) — hidden until
      // the row is flagged; the value is a per-row `comment` string.
      const cWrap = document.createElement('div');
      cWrap.className = 'cl-comment-wrap';
      const cInput = document.createElement('input');
      cInput.type = 'text';
      cInput.className = 'cl-comment-input';
      cInput.placeholder = 'Describe the problem…';
      cInput.value = value[i].comment || '';
      cInput.addEventListener('input', e => {
        const t = e.target.value;
        if (t) value[i].comment = t; else delete value[i].comment;
        ctx.set(field.id, value);
      });
      cWrap.appendChild(cInput);
      const syncComment = () => {
        cWrap.style.display = flagged.includes(value[i].answer) ? '' : 'none';
      };
      syncComment();

      const refs = [];
      options.forEach(opt => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cl-btn';
        b.textContent = opt;
        if (value[i].answer === opt) b.classList.add('active');
        b.addEventListener('click', () => {
          value[i].answer = value[i].answer === opt ? null : opt;   // tap again clears
          refs.forEach(rb => rb.el.classList.toggle('active', value[i].answer === rb.opt));
          syncComment();
          ctx.set(field.id, value);
          syncCount();
        });
        refs.push({ el: b, opt });
        btns.appendChild(b);
      });
      rowBtns.push({ refs, syncComment });
      row.appendChild(btns);
      row.appendChild(cWrap);
      list.appendChild(row);
    });
    markAll.addEventListener('click', () => {
      value.forEach((r, i) => {
        r.answer = options[0];
        rowBtns[i].refs.forEach(rb => rb.el.classList.toggle('active', rb.opt === options[0]));
        rowBtns[i].syncComment();
      });
      ctx.set(field.id, value);
      syncCount();
    });
    syncCount();
    wrapper.appendChild(list);
    ctx.container.appendChild(wrapper);
  },
  validate(field, value) {
    const rows = Array.isArray(value) ? value : [];
    // A flagged answer (default: the 2nd status option) needs its why — this
    // gate applies even when the checklist itself is not required, exactly
    // like the native QAQC fail rule.
    const flagged = checklistFlagged(field);
    const noWhy = rows.find(r => r && flagged.includes(r.answer) && !String(r.comment || '').trim());
    if (noWhy) return `${field.label}: "${noWhy.item}" is marked ${noWhy.answer} — add a comment.`;

    if (!field.required) return null;
    if (!rows.length) return `Please fill in: ${field.label}`;
    const missing = rows.findIndex(r => !r || !r.answer);
    return missing === -1 ? null : `${field.label}: item ${missing + 1} not answered.`;
  },
};

// Repeating-rows table (Onda 1): typed columns (text/number), rows added in
// the field. Value = array of row objects keyed by column key, all strings.
// Everything is closure-scoped — no fixed element ids, so two tables in one
// form can never cross-talk (the ewo-tbl pattern minus its singletons).
const tableHandler = {
  render(field, ctx) {
    const { wrapper } = _wrapper(field);
    const cols = field.columns || [];
    const cap = Math.min(Math.max(parseInt(field.maxRows, 10) || 15, 1), 100);

    let rows = ctx.get(field.id);
    if (!Array.isArray(rows)) rows = [];
    if (!rows.length) rows.push({});
    ctx.set(field.id, rows);

    const gridCols = `repeat(${cols.length}, 1fr) 30px`;
    const tbl = document.createElement('div');
    tbl.className = 'ft-tbl';
    const head = document.createElement('div');
    head.className = 'ft-tbl-head';
    head.style.gridTemplateColumns = gridCols;
    cols.forEach(c => {
      const h = document.createElement('div');
      h.textContent = c.label;
      head.appendChild(h);
    });
    head.appendChild(document.createElement('div'));
    tbl.appendChild(head);
    const body = document.createElement('div');
    tbl.appendChild(body);

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'ft-add-btn';
    addBtn.textContent = '＋ Add row';

    const renderRows = () => {
      body.innerHTML = '';
      rows.forEach((r, i) => {
        const line = document.createElement('div');
        line.className = 'ft-tbl-row';
        line.style.gridTemplateColumns = gridCols;
        cols.forEach(c => {
          const inp = document.createElement('input');
          inp.className = 'field-input ft-cell';
          inp.type = c.type === 'number' ? 'number' : 'text';
          if (c.type === 'number') {
            const cmin = _numberMin(c);
            if (cmin != null) inp.min = String(cmin);
            inp.inputMode = 'decimal';
          }
          inp.value = r[c.key] || '';
          inp.setAttribute('aria-label', `${c.label}, row ${i + 1}`);
          inp.addEventListener('input', e => { r[c.key] = e.target.value; ctx.set(field.id, rows); });
          line.appendChild(inp);
        });
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'ft-del';
        del.textContent = '✕';
        del.setAttribute('aria-label', `Remove row ${i + 1}`);
        del.addEventListener('click', () => {
          rows.splice(i, 1);
          if (!rows.length) rows.push({});
          ctx.set(field.id, rows);
          renderRows();
        });
        line.appendChild(del);
        body.appendChild(line);
      });
      addBtn.disabled = rows.length >= cap;
    };
    addBtn.addEventListener('click', () => {
      if (rows.length >= cap) return;
      rows.push({});
      ctx.set(field.id, rows);
      renderRows();
    });
    renderRows();
    wrapper.appendChild(tbl);
    wrapper.appendChild(addBtn);
    ctx.container.appendChild(wrapper);
  },
  validate(field, value) {
    const rows = Array.isArray(value) ? value : [];
    for (const c of (field.columns || [])) {   // number columns: same floor as number fields
      if (c.type !== 'number') continue;
      for (const r of rows) {
        const msg = r && _numberMsg({ ...c, label: `${field.label} — ${c.label}` }, r[c.key]);
        if (msg) return msg;
      }
    }
    if (!field.required) return null;
    const hasData = rows.some(r => r && Object.values(r).some(v => String(v || '').trim() !== ''));
    return hasData ? null : `Please add at least one row: ${field.label}`;
  },
};

// Autofill from login/context — driven by ctx (field app supplies it; admin
// preview leaves it absent, so nothing prefills). Mirrors renderField's autofill.
function _applyAutofill(field, input, ctx) {
  const c = ctx.context || {};
  if (field.autofill === 'foreman') {
    const name = c.effForemanName || c.foremanName || '';
    input.value = name; ctx.set(field.id, name);
    input.readOnly = true; input.classList.add('field-input--prefilled');
  } else if (field.autofill === 'project_code') {
    const code = (c.project || '').split(/\s*[-–]\s*/)[0].trim();
    input.value = code; ctx.set(field.id, code);
    input.readOnly = true; input.classList.add('field-input--prefilled');
  } else if (field.autofill === 'ewo_number') {
    input.value = 'EWO-…';
    input.readOnly = true; input.classList.add('field-input--prefilled');
    ctx.helpers?.peekEwoNumber?.().then(n => {
      if (n != null) {
        const formatted = 'EWO-' + String(n).padStart(3, '0');
        input.value = formatted; ctx.set(field.id, formatted);
      }
    });
  }
}

export function registerSimpleFields(register) {
  INPUT_TYPES.forEach(t => register(t, inputHandler));
  register('textarea', textareaHandler);
  register('select', selectHandler);
  register('radio', radioHandler);
  register('checkbox-group', checkboxGroupHandler);
  register('section', sectionHandler);
  register('photo', photoHandler);
  register('checklist', checklistHandler);
  register('table', tableHandler);
}
